/**
 * Inline "which field participant" selector for operator command
 * surfaces (SnapshotButton, OperatorToolbar, CameraControl) — backlog
 * §9.4 (2026-09-08).
 *
 * Renders nothing when there's 0 or 1 field participants — the common
 * case renders and behaves exactly as it did before this component
 * existed. Only shows once `participants.length > 1`, matching
 * `useFieldParticipantSelection`'s `showPicker` flag.
 *
 * Labelled with each participant's real display name (from
 * `useParticipantRoleLookup`'s `byParticipantIdName`, keyed by the
 * uuid inside their `participant:<uuid>` LiveKit identity) rather than
 * LiveKit's own `.name`, which the token-minting service sets to the
 * raw identity string — see `participantUuidFromIdentity`.
 */
import type { Participant } from "livekit-client";
import { useTranslation } from "react-i18next";

import { participantUuidFromIdentity } from "@/lib/fieldParticipants";

export function FieldParticipantPicker({
  participants,
  selectedIdentity,
  onChange,
  displayNameByParticipantId,
  className,
}: {
  participants: Participant[];
  selectedIdentity: string | null;
  onChange: (identity: string) => void;
  displayNameByParticipantId?: Record<string, string>;
  className?: string;
}) {
  const { t } = useTranslation();
  if (participants.length <= 1) return null;

  return (
    <label className={className ?? "block text-xs text-nb-text-2"}>
      <span>{t("ui.fieldParticipantPicker.label", "Target participant")}</span>
      <select
        className="input mt-1 w-full"
        value={selectedIdentity ?? ""}
        onChange={(e) => onChange(e.target.value)}
      >
        {participants.map((p, i) => {
          const uuid = participantUuidFromIdentity(p.identity);
          const name = uuid ? displayNameByParticipantId?.[uuid] : undefined;
          return (
            <option key={p.identity} value={p.identity}>
              {name ||
                t("ui.fieldParticipantPicker.fallbackLabel", {
                  defaultValue: "Field participant {{n}}",
                  n: i + 1,
                })}
            </option>
          );
        })}
      </select>
    </label>
  );
}
